import type { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  BookmarkAddOutline,
  BookmarkRemoveOutline,
  PlayCircleOutline,
} from '@eqi-investimentos/icons/web';
import { Button, Flex, Text } from '@eqi-investimentos/react';
import { Divider } from '@chakra-ui/react';
import { Content } from '@/modules/Entertainment/interface/Content';
import { Program } from '@/modules/Entertainment/interface/Program';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useBookmark } from '@/modules/Entertainment/http/client/mutations/useBookmark';
import { getHrefRecentContent } from '@/utils/content';
import { HasRecommendationTag } from './HasRecommendationTag';

export const RecentContentCard: FC<RecentContentCardProps> = ({
  id,
  authorName,
  programTitle,
  publishedAt,
  summary,
  title,
  thumb,
  hasRecommendation,
  isSaved,
  slug,
  object,
  program_type,
  content_count,
  t,
}) => {
  const { mutate, isPending } = useBookmark(['last', 'content']);
  const { mutate: sendClickEvent } = useSendClickEvent();

  let labelQtdContent = '';
  if (content_count) {
    labelQtdContent =
      content_count > 1 ? t('attributes.contents') : t('attributes.content');
  }

  const hrefCard = getHrefRecentContent({
    object: object,
    program_type,
    slug,
  });

  const handleSendClick = () => {
    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.researchContent.label(
        t('attributes.open'),
        hrefCard
      ),
    });
  };

  return (
    <Flex
      pos="relative"
      flexDir="column"
      gap="l"
      w="21.75rem"
      h="31.4375rem"
      p="l"
      border="1px solid"
      borderColor="border.secondary"
      borderRadius="xs"
      transition="0.4s all ease-in-out"
      _hover={{
        bg: 'background.brandLight',
        '#badge-recomendation-header': {
          bg: 'content.brand',
          p: {
            color: 'content.inverse',
          },
        },
      }}
    >
      <Flex
        as={Link}
        href={hrefCard}
        passHref
        onClick={() => handleSendClick()}
        w="full"
        h="10.75rem"
      >
        <Image
          src={thumb ?? ''}
          width={308}
          height={172}
          alt={programTitle ?? ''}
          style={{
            borderRadius: '0.25rem',
            cursor: 'pointer',
            objectFit: 'cover',
          }}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = '/assets/eqi-placeholder.png';
          }}
        />
      </Flex>
      <Flex flexDir="column" minH="12.5rem" gap="s">
        <Flex w="full" justifyContent="space-between" alignItems="center">
          <Text size="l" color="content.tertiary">
            {object === 'program'
              ? `${content_count} ${labelQtdContent}`
              : publishedAt}
          </Text>
          {hasRecommendation && <HasRecommendationTag t={t} />}
        </Flex>
        <Text size="2xl" fontWeight="medium" noOfLines={1}>
          {title}
        </Text>
        <Text color="content.tertiary" fontWeight="light" size="l">
          {getTextProgramTitleAndAuthorName(programTitle, authorName, object)}
        </Text>
        <Text
          size="xl"
          noOfLines={3}
          dangerouslySetInnerHTML={{ __html: summary }}
        />
      </Flex>
      <Divider borderColor="border.secondary" />
      <Flex align="center" justifyContent="space-between">
        <Flex
          as={Link}
          href={hrefCard}
          onClick={() => handleSendClick()}
          color="content.primary"
          gap="xs"
          alignItems="center"
          _hover={{ color: 'content.brand' }}
        >
          <PlayCircleOutline
            aria-label={t('attributes.open')}
            w="24"
            h="24"
            color="content.brand"
            transitionDuration="0ms"
          />
          <Text size="l">{t('attributes.open')}</Text>
        </Flex>
        <Button
          aria-label={isSaved ? t('attributes.saved') : t('attributes.save')}
          leftIcon={
            isSaved ? (
              <BookmarkRemoveOutline color="content.brand" />
            ) : (
              <BookmarkAddOutline color="content.brand" />
            )
          }
          gap="xs"
          variant="naked"
          color="content.primary"
          isLoading={isPending}
          onClick={() => mutate({ id, bookmarked: isSaved, object })}
        >
          <Text size="l">
            {isSaved ? t('attributes.saved') : t('attributes.save')}
          </Text>
        </Button>
      </Flex>
    </Flex>
  );
};

type RecentContentCardProps = {
  id: Content['id'];
  slug: Content['slug'];
  authorName: Content['author_name'];
  programTitle: Content['program_title'];
  publishedAt: Content['published_at'];
  summary: Content['summary'];
  title: Content['title'];
  thumb: Content['thumb'];
  hasRecommendation: boolean;
  isSaved: boolean;
  t: (key: string) => string;
  object: Content['object'];
  program_type: Content['program_type'];
  content_count: Program['content_count'];
};

const getTextProgramTitleAndAuthorName = (
  programTitle: string,
  authorName: string,
  object: string
) => {
  if (object === 'program') {
    if (authorName) {
      return authorName;
    }
    return '';
  }

  if (programTitle && authorName) {
    return `${programTitle} • ${authorName}`;
  }

  return programTitle || authorName || '';
};
